import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Download, Upload, Pencil, Trash, Radical } from "lucide-react";
import { toast } from "react-toastify";

// UI Components
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DeleteModal } from "@/components/delete-modal";
import { Pagination } from "@/components/pagination";

// Hooks & Utils
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";

// Types
type Comercializadora = {
  id: number;
  name: string;
};

type ElectricityFactor = {
  id: number;
  year: number;
  comercializadora: Comercializadora;
  coefficient: number;
};

type ElectricityFactorResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: ElectricityFactor[];
};

import { ElectricityFactorForm } from "./ElectricityFactorForm";
import { AsyncSelect } from "@/components/AsyncSelect";
import { PageHeader } from "@/components/PageHeader";
import ElectricityIcon from "@/components/icons/ElectricityIcon";

export function ElectricityFactorList() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedYear, setSelectedYear] = useState<string>("none");
  const [selectedComercializadora, setSelectedComercializadora] =
    useState<string>("none");
  const queryClient = useQueryClient();
  const [resetKey, setResetKey] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file import
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(
        getApiUrl("electricity-factors/import_data/"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to import electricity factors");
      }

      // Invalidate and refetch
      await queryClient.invalidateQueries({
        queryKey: ["electricity-factors"],
      });

      // Show success message with import stats
      toast.success(
        t("common.importSuccess", {
          created: result.created,
          skipped: result.skipped,
          errors: result.errors || 0,
        }),
      );
    } catch (error) {
      console.error("Import failed:", error);
      toast.error(t("common.importError"));
    } finally {
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setIsImporting(false);
    }
  };

  const PAGE_SIZE = 10;

  // Fetcher function for comercializadoras (used by AsyncSelect)
  const fetchComercializadoras = useCallback(
    async (query?: string): Promise<Comercializadora[]> => {
      try {
        // If query is a number, it's likely an ID lookup for initialization
        const isIdLookup = query && /^\d+$/.test(query);

        if (isIdLookup) {
          // For ID lookups, fetch the specific comercializadora
          const response = await fetch(
            getApiUrl(`/comercializadoras/${query}/`),
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            },
          );
          if (!response.ok) {
            throw new Error("Failed to fetch comercializadora");
          }
          const res = await response.json();
          return [res];
        } else {
          // Search by name
          const searchParam = query ? `&name=${encodeURIComponent(query)}` : "";
          const response = await fetch(
            getApiUrl(`/comercializadoras/?limit=20${searchParam}`),
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            },
          );
          if (!response.ok) {
            throw new Error("Failed to fetch comercializadoras");
          }
          const res = await response.json();
          return res.results || [];
        }
      } catch (error) {
        console.error("Error fetching comercializadoras:", error);
        return [];
      }
    },
    [token],
  );

  const { data, isLoading, error } = useQuery<ElectricityFactorResponse>({
    queryKey: [
      "electricity-factors",
      currentPage,
      selectedYear,
      selectedComercializadora,
    ],
    queryFn: async () => {
      const offset = (currentPage - 1) * PAGE_SIZE;
      let url = `electricity-factors/?limit=${PAGE_SIZE}&offset=${offset}`;

      if (selectedYear !== "none") {
        url += `&year=${selectedYear}`;
      }

      if (selectedComercializadora !== "none") {
        // Use the correct filter name and ensure it's a number
        url += `&comercializadora=${selectedComercializadora}`;
      }

      const response = await fetch(getApiUrl(url), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch electricity factors");
      }
      return response.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(getApiUrl(`electricity-factors/${id}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete electricity factor");
      }
      return id; // Return the deleted ID for use in onSuccess
    },
    onSuccess: (deletedId) => {
      // Invalidate and refetch the list
      queryClient.invalidateQueries({ queryKey: ["electricity-factors"] });

      // Show success message
      toast.success(t("common.success.deleted"));

      // Only close the modal after successful deletion
      setItemToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      // Don't close the modal on error so user can retry
    },
  });

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handleExport = async () => {
    try {
      // Build query parameters based on current filters
      const params = new URLSearchParams();

      if (selectedYear !== "none") {
        params.append("year", selectedYear);
      }

      if (selectedComercializadora !== "none") {
        params.append("comercializadora", selectedComercializadora);
      }

      // Add pagination parameters
      params.append("limit", "99999"); // Export all records

      // Create the export URL
      const exportUrl = `${getApiUrl(`electricity-factors/export/`)}?${params.toString()}`;

      // Make the API request with proper headers
      const response = await fetch(exportUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the response as a blob
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger the download
      const a = document.createElement("a");
      a.href = url;
      a.download = `electricity_factors_export_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);

      // Trigger the download
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error(t("common.exportError"));
    }
  };

  const handleEdit = (id: number) => {
    setEditingId(id);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setEditingId(null);
    setIsDrawerOpen(true);
  };

  if (error) {
    return <div className="text-destructive">Error: {error.message}</div>;
  }

  // Generate year options (current year - 10 to current year + 10)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from(
    { length: 21 },
    (_, i) => currentYear - 10 + i,
  );

  return (
    <div className="flex flex-col h-full pb-8">
      <PageHeader
        icon={
          <ElectricityIcon
            width={180}
            height={180}
            className="w-[50px] h-[50px]"
          />
        }
        actionTitle="coefficients.electricity.add"
        title="coefficients.electricity.title"
        description="coefficients.electricity.description"
        handleNew={handleAdd}
      >
        <Button
          variant="outline"
          onClick={handleExport}
          className="flex items-center"
        >
          <Download className="h-4 w-4 mr-2" />
          {t("common.export")}
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          className="hidden"
          disabled={isImporting}
        />
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center"
          disabled={isImporting}
        >
          {isImporting ? (
            <>
              <span className="h-4 w-4 mr-2 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              {t("common.importing")}
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              {t("common.import")}
            </>
          )}
        </Button>
      </PageHeader>

      <div className="flex items-center space-x-2 p-4">
        <div className="w-48">
          <Select value={selectedYear} onValueChange={handleYearChange}>
            <SelectTrigger>
              <SelectValue placeholder={t("filters.selectYear")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("filters.allYears")}</SelectItem>
              {yearOptions.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2 w-96">
          <div className="flex-1" key={`comercializadora-select-${resetKey}`}>
            <AsyncSelect<Comercializadora>
              value={
                selectedComercializadora === "none"
                  ? ""
                  : selectedComercializadora
              }
              onChange={(value) => {
                // The value will be the ID of the selected option
                setSelectedComercializadora(value || "none");
                setCurrentPage(1);
              }}
              fetcher={fetchComercializadoras}
              label=""
              placeholder={t("filters.allComercializadoras")}
              getOptionValue={(option) => option.id.toString()}
              getDisplayValue={(option) => {
                if (!option) return "";
                // If option is a string, it's already the display value
                if (typeof option === "string") return option;
                // Otherwise, it's a Comercializadora object
                return option.name;
              }}
              renderOption={(option) => (
                <div className="flex items-center">
                  <span>{option.name}</span>
                </div>
              )}
              notFound={
                <div className="py-6 text-center text-sm">
                  {t("common.no_results_found")}
                </div>
              }
              width="100%"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSelectedComercializadora("none");
              setCurrentPage(1);
              // Force AsyncSelect to remount by changing its key
              setResetKey((prev) => prev + 1);
            }}
            disabled={selectedComercializadora === "none"}
            className="whitespace-nowrap mt-1"
          >
            {t("common.reset")}
          </Button>
        </div>
      </div>

      <div className="px-4">
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t("coefficients.electricity.table.year")}
                </TableHead>
                <TableHead>
                  {t("coefficients.electricity.table.comercializadora")}
                </TableHead>
                <TableHead className="text-right">
                  {t("coefficients.electricity.table.coefficient")}
                </TableHead>
                <TableHead className="text-right">
                  {t("common.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array(5)
                    .fill(0)
                    .map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton className="h-4 w-16" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-32" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-4 w-16 ml-auto" />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Skeleton className="h-8 w-8" />
                            <Skeleton className="h-8 w-8" />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                : data?.results.map((factor) => (
                    <TableRow key={factor.id}>
                      <TableCell variant="compact">{factor.year}</TableCell>
                      <TableCell variant="compact">
                        {factor.comercializadora?.name || "-"}
                      </TableCell>
                      <TableCell variant="compact" className="text-center">
                        {factor.coefficient}
                      </TableCell>
                      <TableCell variant="compact" className="text-right">
                        <div className="flex justify-center items-center">
                          <div
                            role="button"
                            className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                            onClick={() => handleEdit(factor.id)}
                          >
                            <Pencil
                              size={30}
                              fill="#000000"
                              stroke="#ffffff"
                              strokeWidth={1}
                            />
                          </div>
                          <div
                            role="button"
                            className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                            onClick={() => setItemToDelete(factor.id)}
                          >
                            <Trash
                              size={30}
                              fill="#ff0000"
                              stroke="#ffffff"
                              strokeWidth={2}
                            />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>

        {data && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            hasNextPage={!!data.next}
            hasPreviousPage={!!data.previous}
          />
        )}
      </div>

      <Drawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-full sm:max-w-md">
          <div className="flex flex-col h-full">
            <DrawerHeader className="border-b">
              <DrawerTitle>
                {editingId
                  ? t("coefficients.electricity.editTitle")
                  : t("coefficients.electricity.addTitle")}
              </DrawerTitle>
              <DrawerDescription>
                {editingId
                  ? t("coefficients.electricity.editDescription")
                  : t("coefficients.electricity.addDescription")}
              </DrawerDescription>
            </DrawerHeader>
            <div className="flex-1 overflow-y-auto p-6">
              <ElectricityFactorForm
                id={editingId || undefined}
                onSuccess={() => {
                  setIsDrawerOpen(false);
                  queryClient.invalidateQueries({
                    queryKey: ["electricity-factors"],
                  });
                }}
                onCancel={() => setIsDrawerOpen(false)}
              />
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <DeleteModal
        open={!!itemToDelete}
        onOpenChange={(open) => !open && setItemToDelete(null)}
        onDelete={() => {
          if (itemToDelete) {
            deleteMutation.mutate(itemToDelete);
          }
        }}
        title={t("coefficients.electricity.deleteTitle")}
        entity={t("coefficients.electricity.title")}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
